import React from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, MessageSquare, XCircle, AlertCircle } from 'lucide-react'

const InstallSuccess: React.FC = () => {
  const [searchParams] = useSearchParams()
  const teamName = searchParams.get('team') || 'Your team'
  const error = searchParams.get('error')

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <XCircle className="h-20 w-20 text-red-500" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Installation Failed
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            There was an issue installing SyncSimple to your workspace
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error Details</h3>
                    <div className="mt-2 text-sm text-red-700">
                      {decodeURIComponent(error)}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  What you can do:
                </h3>
                
                <div className="space-y-3 text-sm text-gray-600">
                  <p>• Try installing the app again using the correct installation link</p>
                  <p>• Make sure you have admin permissions in your Slack workspace</p>
                  <p>• Contact support if the issue persists</p>
                </div>
              </div>

              <div className="flex space-x-4">
                <a
                  href={`https://slack.com/oauth/v2/authorize?client_id=1059890709938.906&scope=app_mentions:read,channels:history,channels:read,chat:write,chat:write.public,commands,files:read,groups:history,groups:read,im:history,im:read,im:write,mpim:read,team:read,users:read,users:read.email,users:write&user_scope=&redirect_uri=https://cfcpcmswpbygvdbyrivt.supabase.co/functions/v1/slack-oauth`}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-center transition duration-150 ease-in-out"
                >
                  Try Again
                </a>
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Need help?{' '}
                  <a href="#" className="text-blue-600 hover:text-blue-500">
                    Contact support
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <CheckCircle className="h-20 w-20 text-green-500" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Installation Successful!
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          SyncSimple has been installed for <strong>{teamName}</strong>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🎉 You're all set! Here's what you can do now:
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <MessageSquare className="h-6 w-6 text-blue-500 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-gray-900">Use the /standup command</h4>
                    <p className="text-sm text-gray-600">
                      Type <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">/standup</code> in any Slack channel or DM to submit your daily standup.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 text-purple-500 mt-0.5 text-center">🎤</div>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-gray-900">Record voice memos</h4>
                    <p className="text-sm text-gray-600">
                      Click the voice memo button in the standup form to record audio that will be automatically transcribed.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 text-green-500 mt-0.5 text-center">📊</div>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-gray-900">View analytics</h4>
                    <p className="text-sm text-gray-600">
                      Check this dashboard to see team standup trends, sentiment analysis, and insights.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="bg-blue-50 rounded-md p-4">
                <div className="text-sm text-blue-800">
                  <strong>Pro tip:</strong> Set up a recurring reminder in your team channel to encourage daily standup submissions. You can use Slack's built-in reminders or create a workflow.
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <Link
                to="/dashboard"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-center transition duration-150 ease-in-out flex items-center justify-center"
              >
                View Dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                Need help? Check out our{' '}
                <a href="#" className="text-blue-600 hover:text-blue-500">
                  documentation
                </a>{' '}
                or{' '}
                <a href="#" className="text-blue-600 hover:text-blue-500">
                  contact support
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InstallSuccess 